import type {
  OverrideListResponse,
  Override,
  Carrier,
  CreateOverrideInput,
  AuditLogResponse,
} from './types';
import * as Sentry from '@sentry/react';

const BASE = import.meta.env.VITE_API_BASE || '/api/v1';
const TOKEN_KEY = 'so_api_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { ...options?.headers as Record<string, string> };
  if (options?.body) {
    headers['Content-Type'] = 'application/json';
  }
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    ...options,
    headers,
  });

  if (res.status === 401) {
    clearToken();
    window.dispatchEvent(new CustomEvent('auth:logout'));
    const data = await res.json();
    Sentry.addBreadcrumb({
      category: 'api',
      message: `401 ${options?.method || 'GET'} ${url}`,
      level: 'warning',
    });
    throw { status: 401, ...data };
  }

  if (res.status === 204) return undefined as T;

  const data = await res.json();

  if (!res.ok) {
    Sentry.addBreadcrumb({
      category: 'api',
      message: `${res.status} ${options?.method || 'GET'} ${url}`,
      data: { message: data.message },
      level: 'error',
    });
    throw { status: res.status, ...data };
  }

  return data;
}

export interface ListParams {
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_order?: string;
  carrier_code?: string;
  fare_family?: string;
  correct_shelve?: string;
  wrong_shelve?: string;
  confirmed?: string;
  gds?: string;
  booking_code?: string;
  search?: string;
}

export async function fetchOverrides(
  params: ListParams = {}
): Promise<OverrideListResponse> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') qs.set(k, String(v));
  }
  return request<OverrideListResponse>(`${BASE}/overrides?${qs}`);
}

export async function fetchOverride(id: number): Promise<Override> {
  return request<Override>(`${BASE}/overrides/${id}`);
}

export async function createOverride(
  input: CreateOverrideInput
): Promise<Override> {
  return request<Override>(`${BASE}/overrides`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateOverride(
  id: number,
  input: CreateOverrideInput
): Promise<Override> {
  return request<Override>(`${BASE}/overrides/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function patchOverride(
  id: number,
  input: Partial<CreateOverrideInput>
): Promise<Override> {
  return request<Override>(`${BASE}/overrides/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export async function deleteOverride(id: number): Promise<void> {
  return request<void>(`${BASE}/overrides/${id}`, { method: 'DELETE' });
}

export async function fetchCarriers(): Promise<Carrier[]> {
  return request<Carrier[]>(`${BASE}/carriers`);
}

export interface AuditLogParams {
  page?: number;
  per_page?: number;
  override_id?: number;
  user_email?: string;
  action?: string;
}

export async function fetchAuditLog(
  params: AuditLogParams = {}
): Promise<AuditLogResponse> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') qs.set(k, String(v));
  }
  return request<AuditLogResponse>(`${BASE}/audit-log?${qs}`);
}

// ─── Token management ────────────────────────────────────

export interface TokenStatus {
  hasUsers: boolean;
  authenticated: boolean;
  role?: string;
  email?: string;
}

export interface TokenInfo {
  id: number;
  name: string;
  email: string;
  role: string;
  revoked: number;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface CreateTokenResult extends TokenInfo {
  token: string;
}

export async function fetchTokenStatus(): Promise<TokenStatus> {
  // Use raw fetch here since /tokens/status is always accessible
  // and we want to check status even without a valid token
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(`${BASE}/tokens/status`, { headers });
  return res.json();
}

export async function fetchTokens(): Promise<TokenInfo[]> {
  return request<TokenInfo[]>(`${BASE}/tokens`);
}

export async function createApiToken(
  input: { name: string; email: string; role: string }
): Promise<CreateTokenResult> {
  return request<CreateTokenResult>(`${BASE}/tokens`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function revokeApiToken(id: number): Promise<void> {
  return request<void>(`${BASE}/tokens/${id}`, { method: 'DELETE' });
}
